import { Clock3 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { useI18n } from '@/lib/i18n'

interface TurnClock {
  serverNowMs: number
  nextTakeoverAtMs: number
}

interface WaitingSeat {
  id: string
  name: string
  takeoverAtMs?: number
}

interface TakeoverCountdownProps {
  turnClock?: TurnClock | null
  waitingOn?: WaitingSeat[]
  variant?: 'panel' | 'inline'
}

const TICK_MS = 1000
const EMPTY_WAITING_SEATS: WaitingSeat[] = []

export function formatTakeoverRemaining(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':')
}

export function TakeoverCountdown({
  turnClock,
  waitingOn = EMPTY_WAITING_SEATS,
  variant = 'panel',
}: TakeoverCountdownProps) {
  const { t } = useI18n()
  const hasValidTurnClock
    = turnClock !== null
      && turnClock !== undefined
      && Number.isFinite(turnClock.serverNowMs)
      && Number.isFinite(turnClock.nextTakeoverAtMs)
  const clockKey = hasValidTurnClock
    ? `${turnClock.serverNowMs}:${turnClock.nextTakeoverAtMs}`
    : ''
  const [tick, setTick] = useState({ clockKey, elapsedMs: 0 })
  const elapsedMs = tick.clockKey === clockKey ? tick.elapsedMs : 0

  useEffect(() => {
    if (!clockKey) {
      setTick({ clockKey: '', elapsedMs: 0 })
      return
    }

    const receivedAt = performance.now()
    const update = () =>
      setTick({
        clockKey,
        elapsedMs: performance.now() - receivedAt,
      })
    update()
    const timer = window.setInterval(update, TICK_MS)
    return () => window.clearInterval(timer)
  }, [clockKey])

  const namedDeadlines = useMemo(
    () =>
      waitingOn
        .filter((seat): seat is WaitingSeat & { takeoverAtMs: number } =>
          Number.isFinite(seat.takeoverAtMs),
        )
        .sort((left, right) => left.takeoverAtMs - right.takeoverAtMs),
    [waitingOn],
  )

  if (!turnClock || !hasValidTurnClock)
    return null

  const countdowns
    = namedDeadlines.length > 0
      ? namedDeadlines.map(seat => ({
          key: seat.id,
          name: seat.name,
          takeoverAtMs: seat.takeoverAtMs,
        }))
      : [
          {
            key: 'anonymous',
            name: null,
            takeoverAtMs: turnClock.nextTakeoverAtMs,
          },
        ]

  return (
    <div
      className={variant === 'inline'
        ? 'min-w-0 space-y-1 text-amber-950 dark:text-amber-100'
        : 'mx-6 mb-4 space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-amber-950 dark:text-amber-100'}
      data-testid="takeover-countdown"
    >
      {countdowns.map((countdown) => {
        const remainingMs
          = countdown.takeoverAtMs - turnClock.serverNowMs - elapsedMs
        const expired = remainingMs <= 0
        const message = expired
          ? countdown.name
            ? t('play.takeoverCountdown.processingNamed', {
                name: countdown.name,
              })
            : t('play.takeoverCountdown.processing')
          : countdown.name
            ? t('play.takeoverCountdown.named', {
                name: countdown.name,
                time: formatTakeoverRemaining(remainingMs),
              })
            : t('play.takeoverCountdown.anonymous', {
                time: formatTakeoverRemaining(remainingMs),
              })

        return (
          <p
            key={countdown.key}
            className="flex items-center gap-2 text-sm font-medium"
            data-expired={expired || undefined}
          >
            <Clock3 aria-hidden="true" className="size-4 shrink-0" />
            <span role="timer">{message}</span>
          </p>
        )
      })}
    </div>
  )
}
